import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import styles from '../LandingPage/PricingFinal.module.css';
import { plansAPI } from '../../api/apiHelper';

const Pricing = () => {
    const [isAnnual, setIsAnnual] = useState(true);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const response = await plansAPI.getPlans();
            
            // Transform API data to match frontend structure
            const transformedPlans = response.data.map(plan => ({
                name: plan.planName,
                monthlyPrice: plan.monthlyPricePerUser,
                annualPrice: plan.annualPricePerUser,
                features: plan.features,
                popular: plan.isPopular
            }));
            
            setPlans(transformedPlans);
            setError(null);
        } catch (err) {
            console.error('Error fetching plans:', err);
            setError('Failed to load pricing plans');
            // Fallback to hardcoded data if API fails
            setPlans([
                {
                    name: 'CRM (Free)',
                    monthlyPrice: 0,
                    annualPrice: 0,
                    features: [
                        'End-to-end Lead, account, and pipeline management',
                        'Auto-fill fields from conversations',
                        'Built-in social media platform integration',
                        'Structured analytics dashboard'
                    ]
                },
                {
                    name: 'Online Meeting Intelligence + CRM',
                    monthlyPrice: 2199,
                    annualPrice: 1899,
                    features: [
                        'Online meeting recording & transcription up to 60 hours',
                        'Online meeting intelligence for all major web conferencing platforms'
                    ]
                },
                {
                    name: 'Phone Call Intelligence + CRM',
                    monthlyPrice: 2199,
                    annualPrice: 1899,
                    features: [
                        'Phone call recording & transcription up to 60 hours',
                        'Call intelligence using virtual, toll-free & IVR numbers'
                    ]
                },
                {
                    name: 'Online + Phone Call Intelligence + CRM',
                    monthlyPrice: 2699,
                    annualPrice: 2299,
                    features: [
                        'Online meeting and phone call recording & transcription up to 60 hours',
                        'Online meeting intelligence for all major web conferencing platforms',
                        'Call intelligence using virtual, toll-free & IVR numbers'
                    ],
                    popular: true
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const keyFeatures = [
        'Transcription in 100+ Languages',
        'Automated AI Summaries and Meeting Notes',
        'Remember Every Conversation with AI Powered Search',
        'Conversation Intelligence with Talk-time and Sentiment Analysis',
        'Auto Call Scoring, AI Coaching, and Rep Performance Dashboard',
        'Sales Methodology Tracker (MEDDIC)',
        'AI Deal Health Scoring and Revenue Forecasting',
        'Built-in CRM with Auto-Push Notes to CRM Records',
        'Integrations For Leading CRMs And Other Software'
    ];

    return (
        <div style={{ margin: 0, padding: 0 }}>
            <main style={{ paddingBottom: '1.5rem' }}>
                {/* Hero Section */ }
                <section className={ styles.hero } style={{ paddingTop: '0.5rem', marginTop: 0 }}>
                    <h1 className={ styles.title }>Galile Pricing & Features</h1>
                    <p className={ styles.subtitle }>
                        Choose the perfect plan for your sales team
                    </p>

                    {/* Toggle */ }
                    <div className={ styles.toggleContainer }>
                        <span className={ `${styles.toggleLabel} ${!isAnnual ? styles.activeLabel : ''}` }>
                            Monthly
                        </span>
                        <button
                            className={ styles.toggle }
                            onClick={ () => setIsAnnual(!isAnnual) }
                            aria-label="Toggle billing period"
                        >
                            <span className={ `${styles.toggleSlider} ${isAnnual ? styles.toggleSliderActive : ''}` } />
                        </button>
                        <span className={ `${styles.toggleLabel} ${isAnnual ? styles.activeLabel : ''}` }>
                            Annually
                        </span>
                    </div>
                </section>

                {/* Pricing Cards */ }
                <section className={ styles.pricingSection }>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                            <p>Loading pricing plans...</p>
                        </div>
                    ) : error ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>
                            <p>{error}</p>
                        </div>
                    ) : (
                        <div className={ styles.pricingGrid }>
                            { plans.map((plan, index) => (
                                <div
                                    key={ index }
                                    className={ `${styles.pricingCard} ${plan.popular ? styles.popularCard : ''}` }
                                >
                                    { plan.popular && (
                                        <div className={ styles.popularBadge }>RECOMMENDED</div>
                                    ) }

                                    <h3 className={ styles.planName }>{ plan.name }</h3>

                                    <div className={ styles.priceSection }>
                                        <div className={ styles.priceWrapper }>
                                            <span className={ styles.currency }>₹</span>
                                            <span className={ styles.price }>
                                                { isAnnual ? plan.annualPrice.toLocaleString('en-IN') : plan.monthlyPrice.toLocaleString('en-IN') }
                                            </span>
                                        </div>
                                        <div className={ styles.priceLabel }>per user/month</div>
                                    </div>

                                    <div className={ styles.buttonContainer }>
                                        <a href="https://calendly.com/viraj-galile/30min" target="_blank" rel="noopener noreferrer" style={ { textDecoration: 'none' } }>
                                            <button className={ styles.ctaButton }>
                                                Get Started
                                            </button>
                                        </a>
                                    </div>

                                    <ul className={ styles.featuresList }>
                                        { plan.features.map((feature, i) => (
                                            <li key={ i } className={ styles.featureItem }>
                                                <CheckCircle2 size={ 18 } className={ styles.checkIcon } />
                                                <span>{ feature }</span>
                                            </li>
                                        )) }
                                    </ul>
                                </div>
                            )) }
                        </div>
                    )}

                    {/* Top-up Section */ }
                    <div className={ styles.topupSection }>
                        <p className={ styles.topupText }>
                            <strong>Optional Top-up Hours:</strong> Additional 30 hours @ ₹999/user/month
                        </p>
                    </div>
                </section>

                {/* Key Features Section */ }
                <section className={ styles.keyFeaturesSection }>
                    <h2 className={ styles.keyFeaturesTitle }>Key Features Across All Conversation Intelligence Plans</h2>
                    <div className={ styles.keyFeaturesGrid }>
                        { keyFeatures.map((feature, index) => (
                            <div key={ index } className={ styles.keyFeatureItem }>
                                <CheckCircle2 size={ 20 } className={ styles.keyFeatureIcon } />
                                <span>{ feature }</span>
                            </div>
                        )) }
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Pricing;